/**
 * 만기 구간 타입
 */
export const MaturityBucket = {
  WITHIN_YEAR: 'WITHIN_YEAR',
  Y_1_1_5: 'Y_1_1_5',
  Y_1_5_2: 'Y_1_5_2',
  Y_2_3: 'Y_2_3',
  Y_3_5: 'Y_3_5',
  Y_5_PLUS: 'Y_5_PLUS',
};

/**
 * 만기 구간 라벨 매핑
 */
export const maturityBucketLabel = {
  [MaturityBucket.WITHIN_YEAR]: '연내',
  [MaturityBucket.Y_1_1_5]: '1~1.5Y',
  [MaturityBucket.Y_1_5_2]: '1.5~2Y',
  [MaturityBucket.Y_2_3]: '2~3Y',
  [MaturityBucket.Y_3_5]: '3~5Y',
  [MaturityBucket.Y_5_PLUS]: '5Y+',
};

/**
 * 만기 구간 순서
 */
export const maturityBucketOrder = [
  MaturityBucket.WITHIN_YEAR,
  MaturityBucket.Y_1_1_5,
  MaturityBucket.Y_1_5_2,
  MaturityBucket.Y_2_3,
  MaturityBucket.Y_3_5,
  MaturityBucket.Y_5_PLUS,
];

/**
 * 채권 유형(CreditType) 라벨 매핑
 */
export const creditTypeLabel = {
  PUBLIC: '공사채',
  BANK: '은행채',
  CREDIT_FINANCE: '여전채',
  COMPANY: '회사채',
};

/**
 * 채권 유형 순서
 */
export const creditTypeOrder = ['PUBLIC', 'BANK', 'CREDIT_FINANCE', 'COMPANY'];

/**
 * 기관 유형(InstitutionType) 라벨 매핑
 */
export const institutionTypeLabel = {
  SECURITIES: '증권사',
  FOREIGNER: '외국인',
  BANK: '은행',
  INSURANCE_FUND: '보험+기금',
  ASSET_MANAGEMENT_PUBLIC: '운용(공모)',
  ASSET_MANAGEMENT_PRIVATE: '운용(사모)',
  GOVERNMENT: '정부',
  CORPORATION: '기타법인',
  SECURITIES_MUTUAL: '종금/상호',
  INDIVIDUAL: '개인',
};

/**
 * 기관 유형 순서
 */
export const institutionTypeOrder = [
  'SECURITIES',
  'FOREIGNER',
  'BANK',
  'INSURANCE_FUND',
  'ASSET_MANAGEMENT_PUBLIC',
  'ASSET_MANAGEMENT_PRIVATE',
  'GOVERNMENT',
  'CORPORATION',
  'SECURITIES_MUTUAL',
  'INDIVIDUAL',
];

/**
 * 그룹 타입
 */
export const GroupType = {
  BOND_TYPE: 'BOND_TYPE',
  INSTITUTION_TYPE: 'INSTITUTION_TYPE',
};

/**
 * 만기 구간 계산
 * @param {number} remainingMonths - 잔여 개월 수
 * @returns {string} 만기 구간
 */
export function getMaturityBucket(remainingMonths) {
  if (remainingMonths <= 11) return MaturityBucket.WITHIN_YEAR;
  if (remainingMonths <= 17) return MaturityBucket.Y_1_1_5;
  if (remainingMonths <= 23) return MaturityBucket.Y_1_5_2;
  if (remainingMonths <= 35) return MaturityBucket.Y_2_3;
  if (remainingMonths <= 59) return MaturityBucket.Y_3_5;
  return MaturityBucket.Y_5_PLUS;
}

/**
 * 방향 필터 통과 여부 체크
 * @param {Object} record - 채권 수급 레코드
 * @param {Object} directionFilter - 방향 필터 { buy, sell, net }
 * @returns {boolean}
 */
export function passDirectionFilter(record, directionFilter) {
  const isBuy = record.flowType === 'IN';
  const isSell = record.flowType === 'OUT';

  const { buy: wantsBuy, sell: wantsSell, net: wantsNet } = directionFilter;

  // buy와 sell 모두 false이면서 net도 false면 => 아무것도 선택 안 한 상태
  if (!wantsBuy && !wantsSell && !wantsNet) return false;

  // buy/sell 둘 다 false인데 net만 true인 케이스는
  // 모든 IN/OUT 허용 (나중에 순매수 계산에만 쓴다)
  if (!wantsBuy && !wantsSell && wantsNet) {
    return true;
  }

  // buy만 선택했는데 매도 레코드면 제외
  if (!wantsBuy && isBuy) return false;
  // sell만 선택했는데 매수 레코드면 제외
  if (!wantsSell && isSell) return false;

  return true;
}

/**
 * 시장 필터 통과 여부 체크
 * @param {Object} record - 채권 수급 레코드
 * @param {Object} marketFilter - 시장 필터 { primary, secondary }
 * @returns {boolean}
 */
export function passMarketFilter(record, marketFilter) {
  const { primary: wantsPrimary, secondary: wantsSecondary } = marketFilter;

  // 둘 다 선택 안 했으면 제외
  if (!wantsPrimary && !wantsSecondary) return false;

  // 발행시장만 원하는데 유통시장 레코드면 제외
  if (record.marketType === 'PRIMARY' && !wantsPrimary) return false;
  // 유통시장만 원하는데 발행시장 레코드면 제외
  if (record.marketType === 'SECONDARY' && !wantsSecondary) return false;

  return true;
}

/**
 * 행 키 계산
 * @param {Object} record - 채권 수급 레코드
 * @param {string} groupType - 그룹 타입
 * @returns {string}
 */
export function getRowKey(record, groupType) {
  if (groupType === GroupType.BOND_TYPE) {
    // 행 = 기관 유형
    return record.institutionType;
  } else {
    // groupType === GroupType.INSTITUTION_TYPE
    // 행 = 채권 유형(issuer.creditType)
    return record.issuer.creditType;
  }
}

/**
 * 열 키 계산
 * @param {Object} record - 채권 수급 레코드
 * @returns {string}
 */
export function getColKey(record) {
  return getMaturityBucket(record.remainingMonths);
}

/**
 * 금액을 억 단위로 변환
 * @param {number} amount - 원 단위 금액
 * @returns {number} 억 단위 금액
 */
export function toEok(amount) {
  return amount / 100_000_000;
}

/**
 * 금액 포맷팅 (억 단위, 소수점 1자리)
 * @param {number} amount - 억 단위 금액
 * @returns {string}
 */
export function formatAmount(amount) {
  if (amount === 0) return '';
  return amount.toFixed(1);
}

